// src/features/execucao/components/ExecucaoPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import styles from "./ExecucaoPage.module.css";

import { listarProgramacoes } from "../api";
import { on as rtOn, off as rtOff, RT } from "../../../api/realtime";
import FichaExecucaoModal from "../popups/FichaExecucaoModal";
import ExecucaoCard from "./ExecucaoCard";

/* Datas (helpers sem libs) */
import { toKeyYYYYMMDD, todayKeyBR } from "../../../utils/datesBR";

/* Helpers */
const norm   = (v) => (v == null ? "" : String(v).trim());
const normUp = (v) => norm(v).toUpperCase();
const uniq   = (arr) => Array.from(new Set((arr || []).filter(Boolean)));
const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

function parseYMD(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

function formatYMD(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfMonthUTC(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12, 0, 0));
}

function addDaysUTC(date, days) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days, 12, 0, 0));
}

function addMonthsUTC(date, months) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, 12, 0, 0));
}

function toBRDate(ymd) {
  const dt = parseYMD(ymd);
  if (!dt || Number.isNaN(dt.getTime())) return ymd || "";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
  }).format(dt);
}

function monthLabelPtBR(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const text = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/* Ordem de status para filtros */
const ORDER_STATUS = [
  "em_andamento",
  "primeira_previa",
  "segunda_previa",
  "executado",
  "reprogramar",
  "finalizado",
];

/* Rótulos bonitos de status */
const STATUS_LABEL = {
  em_andamento:   "Não Iniciado",
  primeira_previa:"Primeira Prévia",
  segunda_previa: "Segunda Prévia",
  executado:      "Executado",
  reprogramar:    "Reprogramar",
  finalizado:     "Finalizado",
};

function buildDayRangeBR(keyYYYYMMDD) {
  const k = toKeyYYYYMMDD(keyYYYYMMDD);
  if (!k) return { inicioBR: "", fimBR: "" };
  return { inicioBR: `${k} 00:00:00`, fimBR: `${k} 23:59:59` };
}

/* Ícone "Filter Off" */
function FilterOffIcon({ size = 18, stroke = 2 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 5h18l-7 8v4l-4 3v-7L3 5z" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

/* ================= MultiSelect (aceita string ou {value,label}) ================ */
function MultiSelect({ options = [], value, onChange, placeholder = "Todos" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const setSelected = (next) => onChange(Array.isArray(value) ? next : next[0] || "");

  const label = selected.length
    ? `${selected.length} selecionado${selected.length > 1 ? "s" : ""}`
    : placeholder;

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const getVal   = (opt) => (typeof opt === "string" ? opt : opt?.value);
  const getLabel = (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? "");

  const handleItem = (opt) => {
    const val = getVal(opt);
    const s = new Set(selected);
    if (s.has(val)) s.delete(val);
    else s.add(val);
    setSelected(Array.from(s));
  };

  return (
    <div className={styles.msRoot} ref={rootRef}>
      <button
        className={`${styles.msBtn} ${open ? styles.open : ""}`}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.msText}>{label}</span>
        <span className={styles.caret}>▾</span>
      </button>

      {open && (
        <div className={styles.msPanel}>
          <div className={styles.msHeader}>
            <span style={{ fontWeight: 800 }}>Selecione</span>
          </div>

          <div className={styles.msListWrap}>
            <div className={styles.msList}>
              {options.map((opt) => {
                const val = getVal(opt);
                const lab = getLabel(opt);
                const id = `ms-${placeholder}-${val}`;
                const checked = selected.includes(val);
                return (
                  <label
                    key={val}
                    className={styles.msItem}
                    htmlFor={id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      className={styles.msCheck}
                      checked={checked}
                      onChange={() => handleItem(opt)}
                    />
                    <span className={styles.msLabel}>{lab}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SidebarMultiSelect = React.memo(function SidebarMultiSelect({
  options = [],
  value,
  onChange,
  placeholder = "Todos",
  title = "Selecione",
  searchable = false,
  searchPlaceholder = "Buscar...",
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const setSelected = (next) => onChange(Array.isArray(value) ? next : next[0] || "");
  const getVal = useCallback((opt) => (typeof opt === "string" ? opt : opt?.value), []);
  const getLabel = useCallback(
    (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? ""),
    []
  );
  const normalizedOptions = useMemo(
    () =>
      options
        .map((opt) => ({ value: getVal(opt), label: getLabel(opt) }))
        .filter((opt) => opt.value !== undefined && opt.value !== null && opt.value !== ""),
    [getLabel, getVal, options]
  );
  const selectedLabels = useMemo(() => {
    const labels = normalizedOptions
      .filter((opt) => selectedSet.has(opt.value))
      .map((opt) => opt.label)
      .filter(Boolean);
    if (labels.length) return labels;
    return selected.map((item) => String(item ?? "").trim()).filter(Boolean);
  }, [normalizedOptions, selected, selectedSet]);
  const label =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      return;
    }

    const updateDirection = () => {
      const anchorEl = rootRef.current;
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      const sidebarRect = anchorEl.closest("aside")?.getBoundingClientRect?.() || null;
      const estimatedHeight = searchable ? 344 : 288;
      const spaceBelow = sidebarRect ? sidebarRect.bottom - rect.bottom : window.innerHeight - rect.bottom;
      const spaceAbove = sidebarRect ? rect.top - sidebarRect.top : rect.top;
      setOpenUp(spaceBelow < estimatedHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, searchable]);

  const normalizedQuery = useMemo(() => normalizeSearchText(query), [query]);
  const filteredOptions = useMemo(() => {
    if (!searchable || !normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((opt) =>
      normalizeSearchText(opt.label).includes(normalizedQuery)
    );
  }, [normalizedOptions, normalizedQuery, searchable]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    if (!searchable) return undefined;
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open, searchable]);

  const handleItem = (opt) => {
    const val = getVal(opt);
    const s = new Set(selected);
    if (s.has(val)) s.delete(val);
    else s.add(val);
    setSelected(Array.from(s));
  };

  const handleClear = () => {
    setSelected([]);
    setQuery("");
  };

  return (
    <div className={styles.msRoot} ref={rootRef}>
      <button
        className={`${styles.msBtn} ${open ? styles.open : ""}`}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.msBtnText}>
          <span className={`${styles.msText} ${selectedLabels.length ? "" : styles.isPlaceholder}`.trim()}>
            {label}
          </span>
          {selectedLabels.length > 1 ? (
            <span className={styles.msMeta}>{selectedLabels.length} selecionados</span>
          ) : null}
        </span>
        <span className={styles.caret}>â–¾</span>
      </button>

      {open && (
        <div className={`${styles.msPanel} ${openUp ? styles.msPanelUp : ""}`.trim()}>
          <div className={styles.msHeader}>
            <div className={styles.msHeaderCopy}>
              <span className={styles.msHeaderTitle}>{title}</span>
              <span className={styles.msHeaderMeta}>
                {selectedLabels.length
                  ? `${selectedLabels.length} marcado${selectedLabels.length > 1 ? "s" : ""}`
                  : "Nenhum marcado"}
              </span>
            </div>
            <button type="button" className={styles.msActionBtn} onClick={handleClear}>
              Limpar
            </button>
          </div>

          {searchable && (
            <div className={styles.msSearchWrap}>
              <input
                ref={searchRef}
                type="text"
                className={styles.msSearch}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
          )}

          <div className={styles.msListWrap}>
            <div className={styles.msList}>
              {filteredOptions.map((opt) => {
                const val = getVal(opt);
                const lab = getLabel(opt);
                const checked = selectedSet.has(val);
                return (
                  <button
                    type="button"
                    key={val}
                    className={`${styles.msItem} ${checked ? styles.msItemSelected : ""}`.trim()}
                    onClick={() => handleItem(opt)}
                    aria-pressed={checked}
                  >
                    <span className={styles.msLabel}>{lab}</span>
                  </button>
                );
              })}
              {!filteredOptions.length && (
                <div className={styles.msEmpty}>Nenhum resultado encontrado.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

function SidebarDateDropdown({
  value,
  onChange,
  ariaLabel = "Selecionar data",
}) {
  const rootRef = useRef(null);
  const selectedDate = useMemo(() => parseYMD(value) || parseYMD(todayKeyBR()), [value]);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonthUTC(selectedDate));

  useEffect(() => {
    if (!open) setViewMonth(startOfMonthUTC(selectedDate));
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selectedYMD = formatYMD(selectedDate);
  const todayYMD = todayKeyBR();
  const firstWeekday = viewMonth.getUTCDay();
  const gridStart = addDaysUTC(viewMonth, -firstWeekday);
  const calendarDays = Array.from({ length: 42 }, (_, index) => addDaysUTC(gridStart, index));

  const handlePick = (ymd) => {
    onChange(ymd);
    setOpen(false);
  };

  return (
    <div className={`${styles.dateDropdown} ${open ? styles.dateDropdownOpen : ""}`.trim()} ref={rootRef}>
      <button
        className={styles.dateButton}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={styles.dateButtonValue}>{toBRDate(selectedYMD)}</span>
      </button>

      {open && (
        <div className={styles.datePanel} role="dialog" aria-label="Calendário">
          <div className={styles.datePanelHead}>
            <button
              className={styles.dateNav}
              type="button"
              onClick={() => setViewMonth((prev) => addMonthsUTC(prev, -1))}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className={styles.datePanelTitle}>{monthLabelPtBR(viewMonth)}</div>
            <button
              className={styles.dateNav}
              type="button"
              onClick={() => setViewMonth((prev) => addMonthsUTC(prev, 1))}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className={styles.dateWeekdays}>
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className={styles.dateWeekday}>
                {weekday}
              </span>
            ))}
          </div>

          <div className={styles.dateGrid}>
            {calendarDays.map((day) => {
              const ymd = formatYMD(day);
              const isOutside = day.getUTCMonth() !== viewMonth.getUTCMonth();
              const isSelected = ymd === selectedYMD;
              const isToday = ymd === todayYMD;

              return (
                <button
                  key={ymd}
                  className={[
                    styles.dateDay,
                    isOutside ? styles.dateDayOutside : "",
                    isSelected ? styles.dateDaySelected : "",
                    isToday ? styles.dateDayToday : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  type="button"
                  onClick={() => handlePick(ymd)}
                  aria-pressed={isSelected}
                >
                  {day.getUTCDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.datePanelFoot}>
            <button
              className={styles.dateAction}
              type="button"
              onClick={() => handlePick(todayYMD)}
            >
              Hoje
            </button>
            <button
              className={`${styles.dateAction} ${styles.dateActionGhost}`}
              type="button"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
/* ============================================================= */

export default function ExecucaoPage({ onBack }) {
  const hojeKey = todayKeyBR();

  const [programacoes, setProgramacoes] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [dataFiltro, setDataFiltro] = useState(hojeKey);
  const [loading, setLoading] = useState(true);

  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const [filGerencias, setFilGerencias] = useState([]);
  const [filCoordenadores, setFilCoordenadores] = useState([]);
  const [filAtividades, setFilAtividades] = useState([]);
  const [filAtivos, setFilAtivos] = useState([]);
  const [filStatus, setFilStatus] = useState([]);

  const carregarProgramacoes = useCallback(() => {
    setLoading(true);
    const k = toKeyYYYYMMDD(dataFiltro) || hojeKey;
    const { inicioBR, fimBR } = buildDayRangeBR(k);
    const payload = {
      etapa: "execucao",
      include_finalizados: 1,
      data: k,
      dataKey: k,
      dia: k,
      data_inicio: inicioBR,
      data_fim: fimBR,
      inicio: inicioBR,
      fim: fimBR,
    };
    listarProgramacoes(payload)
      .then((list) => setProgramacoes(Array.isArray(list) ? list : []))
      .catch(() => setProgramacoes([]))
      .finally(() => setLoading(false));
  }, [dataFiltro, hojeKey]);

  /* Carrega na montagem e quando a data mudar (NÃO ao abrir/fechar modal) */
  useEffect(() => { carregarProgramacoes(); }, [carregarProgramacoes]);

  /* Realtime com debounce para atualizar sem “piscar” */
  useEffect(() => {
    const evts = [RT.CREATED, RT.UPDATED, RT.MOVED, RT.DELETED];
    let t = null;
    const refresh = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { carregarProgramacoes(); t = null; }, 250);
    };
    evts.forEach((e) => rtOn(e, refresh));
    return () => {
      evts.forEach((e) => rtOff(e, refresh));
      if (t) clearTimeout(t);
    };
  }, [carregarProgramacoes]);

  /* Status helper */
  const getStatusKey = useCallback((p) => {
    const wf = (p?.status_workflow || "").toLowerCase();
    const fin = (p?.status_finalizacao || "").toLowerCase();
    if (/finaliz/.test(fin) || /finaliz/.test(wf)) return "finalizado";
    if (/reprog/.test(fin)  || /reprog/.test(wf))  return "reprogramar";
    if (/execut/.test(fin)  || /execut/.test(wf))  return "executado";
    if (/segunda/.test(fin) || fin === "2" || fin === "segunda_previa")   return "segunda_previa";
    if (/primeira/.test(fin) || fin === "1" || fin === "primeira_previa")  return "primeira_previa";
    if ((p?.segunda_previa || "").trim())  return "segunda_previa";
    if ((p?.primeira_previa || "").trim()) return "primeira_previa";
    if (/andamento/.test(fin) || /andamento/.test(wf)) return "em_andamento";
    return "em_andamento";
  }, []);

  /* ======= OPTIONS ======= */
  const gerenciaOptions = useMemo(
    () => uniq(programacoes.map((p) => norm(p.gerencia))),
    [programacoes]
  );
  const coordenadorOptions = useMemo(
    () => uniq(programacoes.map((p) => norm(p.coordenador))),
    [programacoes]
  );
  const atividadeOptions = useMemo(
    () => uniq(programacoes.map((p) => norm(p.atividade))),
    [programacoes]
  );
  const ativoOptions = useMemo(
    () => uniq(programacoes.map((p) => norm(p.ativo))),
    [programacoes]
  );

  const statusKeysPresent = useMemo(() => {
    const keys = uniq(programacoes.map((p) => getStatusKey(p)));
    return keys.sort((a, b) => ORDER_STATUS.indexOf(a) - ORDER_STATUS.indexOf(b));
  }, [programacoes, getStatusKey]);

  // Agora gera [{ value, label }] usando STATUS_LABEL
  const statusOptions = useMemo(
    () =>
      ORDER_STATUS
        .filter((k) => statusKeysPresent.includes(k))
        .map((k) => ({ value: k, label: STATUS_LABEL[k] || k })),
    [statusKeysPresent]
  );

  /* ======= FILTROS ======= */
  const baseFiltradaOutros = useMemo(() => {
    const gset = new Set(filGerencias.map(normUp));
    const cset = new Set(filCoordenadores.map(normUp));
    const aset = new Set(filAtividades.map(normUp));
    const iset = new Set(filAtivos.map(normUp));
    return programacoes.filter((p) => {
      if (gset.size && !gset.has(normUp(p.gerencia))) return false;
      if (cset.size && !cset.has(normUp(p.coordenador))) return false;
      if (aset.size && !aset.has(normUp(p.atividade))) return false;
      if (iset.size && !iset.has(normUp(p.ativo))) return false;
      return true;
    });
  }, [programacoes, filGerencias, filCoordenadores, filAtividades, filAtivos]);

  const visiveis = useMemo(() => {
    const statusSet = new Set(filStatus.map(normUp));
    return !statusSet.size
      ? baseFiltradaOutros
      : baseFiltradaOutros.filter((p) => statusSet.has(normUp(getStatusKey(p))));
  }, [baseFiltradaOutros, filStatus, getStatusKey]);

  const relatorio = useMemo(() => {
    const total = visiveis.length;
    const finalizados = visiveis.reduce(
      (acc, p) => acc + (getStatusKey(p) === "finalizado" ? 1 : 0),
      0
    );
    return { total, finalizados, aExecutar: total - finalizados };
  }, [visiveis, getStatusKey]);

  /* Data: agora permite qualquer data válida, inclusive meses anteriores */
  const handleDataFiltroChange = (nextValue) => {
    const raw = typeof nextValue === "string" ? nextValue : nextValue?.target?.value;
    const picked = toKeyYYYYMMDD(raw);
    setDataFiltro(picked || hojeKey);
  };

  /* ====== Sidebar: posições escaladas (base 1080) ====== */
  const sidebarRef = useRef(null);
  useLayoutEffect(() => {
    const apply = () => {
      const el = sidebarRef.current;
      if (!el) return;

      const h = el.clientHeight || window.innerHeight;
      const BASE = 1080;
      const s = h / BASE;

      const map = {
        "--y-menu": 46 * s,

        "--y-title-data":  (216 - 26) * s, "--y-data":   216 * s,
        "--y-title-ger":   (312 - 26) * s, "--y-ger":    312 * s,
        "--y-title-coord": (408 - 26) * s, "--y-coord":  408 * s,
        "--y-title-ati":   (504 - 26) * s, "--y-ati":    504 * s,
        "--y-title-ativos":(600 - 26) * s, "--y-ativos": 600 * s,
        "--y-title-status":(696 - 26) * s, "--y-status": 696 * s,
      };

      // Âncora no rodapé (mais pra baixo)
      const CTRL_H = 42;
      const GAP_MINI_LIMPAR = 16;
      const BOTTOM_MARGIN = 22;

      const yLimpar = h - BOTTOM_MARGIN - CTRL_H;         // botão "Limpar filtros"
      const yMini   = yLimpar - GAP_MINI_LIMPAR - CTRL_H; // linha dos minis logo acima

      Object.entries(map).forEach(([k, v]) =>
        el.style.setProperty(k, `${Math.round(v)}px`)
      );

      el.style.setProperty("--y-mini", `${Math.round(yMini)}px`);
      el.style.setProperty("--y-limpar", `${Math.round(yLimpar)}px`);
    };

    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  return (
    <div className={styles.root}>
      {/* ===== SIDEBAR fixa (sem scroll) ===== */}
      <aside className={styles.sidebar} ref={sidebarRef}>
        <button type="button" className={`${styles.btn} ${styles.abs} ${styles.posMenu}`} onClick={onBack}>
          Menu Principal
        </button>

        {/* Filtrar Data */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleData}`}>Filtrar Data</div>
        <div className={`${styles.abs} ${styles.posData}`}>
          <SidebarDateDropdown
            value={dataFiltro}
            onChange={handleDataFiltroChange}
            ariaLabel="Selecionar data"
          />
        </div>

        {/* Filtrar Gerências */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleGer}`}>Filtrar Gerências</div>
        <div className={`${styles.abs} ${styles.posGer}`}>
          <SidebarMultiSelect
            options={gerenciaOptions}
            value={filGerencias}
            onChange={setFilGerencias}
            placeholder="Todas as gerências"
            title="Filtrar Gerências"
            searchable
            searchPlaceholder="Buscar gerência..."
          />
        </div>

        {/* Filtrar Coordenadores */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleCoord}`}>Filtrar Coordenadores</div>
        <div className={`${styles.abs} ${styles.posCoord}`}>
          <SidebarMultiSelect
            options={coordenadorOptions}
            value={filCoordenadores}
            onChange={setFilCoordenadores}
            placeholder="Todos os coordenadores"
            title="Filtrar Coordenadores"
            searchable
            searchPlaceholder="Buscar coordenador..."
          />
        </div>

        {/* Filtrar Atividades */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleAti}`}>Filtrar Atividades</div>
        <div className={`${styles.abs} ${styles.posAti}`}>
          <SidebarMultiSelect
            options={atividadeOptions}
            value={filAtividades}
            onChange={setFilAtividades}
            placeholder="Todas as atividades"
            title="Filtrar Atividades"
            searchable
            searchPlaceholder="Buscar atividade..."
          />
        </div>

        {/* Filtrar Ativos */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleAtivos}`}>Filtrar Ativos</div>
        <div className={`${styles.abs} ${styles.posAtivos}`}>
          <SidebarMultiSelect
            options={ativoOptions}
            value={filAtivos}
            onChange={setFilAtivos}
            placeholder="Todos os ativos"
            title="Filtrar Ativos"
            searchable
            searchPlaceholder="Buscar ativo..."
          />
        </div>

        {/* Filtrar Status (usa { value, label } p/ rótulos bonitos) */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleStatus}`}>Filtrar Status</div>
        <div className={`${styles.abs} ${styles.posStatus}`}>
          <SidebarMultiSelect
            options={statusOptions}     // [{ value, label }]
            value={filStatus}           // array de values (chaves do banco)
            onChange={setFilStatus}
            placeholder="Todos os status"
            title="Filtrar Status"
          />
        </div>

        {/* Minis 72x42 */}
        <div className={`${styles.abs} ${styles.mini} ${styles.posMiniProg}`}>
          <div className={styles.miniLbl}>Prog</div>
          <div className={styles.miniVal}>{relatorio.total}</div>
        </div>
        <div className={`${styles.abs} ${styles.mini} ${styles.posMiniFin}`}>
          <div className={styles.miniLbl}>Fin</div>
          <div className={styles.miniVal}>{relatorio.finalizados}</div>
        </div>
        <div className={`${styles.abs} ${styles.mini} ${styles.posMiniExec}`}>
          <div className={styles.miniLbl}>Exec</div>
          <div className={styles.miniVal}>{relatorio.aExecutar}</div>
        </div>

        {/* Limpar filtros */}
        <button
          type="button"
          className={`${styles.btn} ${styles.btnIcon} ${styles.abs} ${styles.posLimpar}`}
          onClick={() => {
            setFilGerencias([]); setFilCoordenadores([]); setFilAtividades([]); setFilAtivos([]); setFilStatus([]);
            setDataFiltro(hojeKey);
          }}
          title="Limpar filtros"
        >
          <FilterOffIcon size={18} />
          Limpar filtros
        </button>
      </aside>

      {/* ===== CONTEÚDO (rola) ===== */}
      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <h2 className={styles.title}>Programações em Execução</h2>
        </div>

        {visiveis.length === 0 ? (
          <div className={styles.emptyState}>Nenhuma programação em execução.</div>
        ) : (
          <div className={`${styles.grid} ${loading ? styles.gridRefreshing : ""}`}>
            {visiveis.map((p) => (
              <ExecucaoCard
                key={p.codigo_unico || p.id}
                programacao={p}
                nowTs={nowTs}
                onSelect={(item) => setSelecionada(item)}
              />
            ))}
          </div>
        )}
      </div>

      {selecionada && (
        <FichaExecucaoModal
          open
          programacao={selecionada}
          onClose={() => setSelecionada(null)}
          onSaved={() => { setSelecionada(null); carregarProgramacoes(); }}
        />
      )}
    </div>
  );
}
